import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { 
  X, 
  Search, 
  Plus,
  ChevronDown,
  ChevronRight,
  FileText,
  Edit3,
  Trash2,
  Save,
  ArrowLeft
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || import.meta.env.VITE_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

const EMPTY_FORM = {
  title: { fr: "", en: "" },
  description: { fr: "", en: "" },
  content: { fr: "", en: "" },
  category: "Objet du contrat",
  is_mandatory: false,
  tags: []
};

export default function ClauseLibrary({ isOpen, onClose, onAddClause, language }) {
  const [clauses, setClauses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedCategories, setExpandedCategories] = useState({});
  // CRUD state
  const [view, setView] = useState("list"); // "list" | "form"
  const [editingClause, setEditingClause] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchClauses();
      fetchCategories();
      setView("list");
    }
  }, [isOpen]);

  const fetchClauses = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('lawliet_token');
      const response = await axios.get(`${API}/clauses/library`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClauses(response.data.clauses || []);
      const initialExpanded = {};
      (response.data.clauses || []).forEach(clause => {
        initialExpanded[clause.category] = true;
      });
      setExpandedCategories(initialExpanded);
    } catch (error) {
      toast.error(language === "fr" ? "Erreur lors du chargement" : "Error loading");
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const token = localStorage.getItem('lawliet_token');
      const response = await axios.get(`${API}/clauses/library/categories`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCategories(response.data.categories || []);
    } catch {}
  };

  const openCreate = () => {
    setEditingClause(null);
    setForm(EMPTY_FORM);
    setView("form");
  };

  const openEdit = (clause) => {
    setEditingClause(clause);
    setForm({
      title: typeof clause.title === 'object' ? clause.title : { fr: clause.title, en: clause.title },
      description: typeof clause.description === 'object' ? clause.description : { fr: clause.description || "", en: "" },
      content: typeof clause.content === 'object' ? clause.content : { fr: clause.content || "", en: "" },
      category: clause.category,
      is_mandatory: clause.is_mandatory || false,
      tags: clause.tags || []
    });
    setView("form");
  };

  const handleSave = async () => {
    if (!form.title.fr?.trim()) { toast.error("Le titre est requis"); return; }
    if (!form.content.fr?.trim()) { toast.error("Le contenu est requis"); return; }
    setSaving(true);
    try {
      const token = localStorage.getItem('lawliet_token');
      if (editingClause) {
        await axios.put(`${API}/clauses/library/${editingClause.id}`, form, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Clause mise à jour");
      } else {
        await axios.post(`${API}/clauses/library`, form, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Clause créée");
      }
      setView("list");
      fetchClauses();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur");
    } finally { setSaving(false); }
  };

  const handleDelete = async (clause) => {
    if (!window.confirm(`Supprimer la clause "${getLocalizedText(clause.title)}" ?`)) return;
    try {
      const token = localStorage.getItem('lawliet_token');
      await axios.delete(`${API}/clauses/library/${clause.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Clause supprimée");
      fetchClauses();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur");
    }
  };

  const getLocalizedText = (text) => {
    if (typeof text === 'object' && text !== null) {
      return text[language] || text['fr'] || text['en'] || Object.values(text)[0];
    }
    return text;
  };

  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({ ...prev, [category]: !prev[category] }));
  };

  const clausesByCategory = clauses.reduce((acc, clause) => {
    const cat = clause.category;
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(clause);
    return acc;
  }, {});

  const filteredClauses = searchQuery
    ? clauses.filter(clause => {
        const title = getLocalizedText(clause.title).toLowerCase();
        const description = getLocalizedText(clause.description).toLowerCase();
        const query = searchQuery.toLowerCase();
        return title.includes(query) || description.includes(query);
      })
    : clauses;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/50 z-40 lg:hidden backdrop-blur-sm"
          />

          <motion.div
            initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 30, stiffness: 300 }}
            className="fixed right-0 top-0 h-full w-full sm:w-[480px] bg-white shadow-2xl z-50 flex flex-col"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b bg-slate-50">
              <div className="flex items-center gap-2">
                {view === "form" && (
                  <button onClick={() => setView("list")} className="p-1.5 hover:bg-slate-200 rounded-lg">
                    <ArrowLeft className="h-4 w-4 text-slate-600" />
                  </button>
                )}
                <div>
                  <h3 className="font-semibold text-slate-900">
                    {view === "form"
                      ? (editingClause ? "Modifier la clause" : "Nouvelle clause")
                      : (language === "fr" ? "Bibliothèque de Clauses" : "Clause Library")}
                  </h3>
                  {view === "list" && (
                    <p className="text-xs text-slate-600 mt-0.5">
                      {clauses.length} clause(s) · cliquez pour ajouter
                    </p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {view === "list" && (
                  <Button size="sm" onClick={openCreate} className="gap-1 bg-blue-600 hover:bg-blue-700 text-xs h-8">
                    <Plus className="h-3.5 w-3.5" /> Nouvelle
                  </Button>
                )}
                <button onClick={onClose} className="p-2 hover:bg-slate-200 rounded-lg transition">
                  <X className="h-5 w-5 text-slate-500" />
                </button>
              </div>
            </div>

            {/* === LIST VIEW === */}
            {view === "list" && (
              <>
                <div className="p-4 border-b">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Rechercher..." className="pl-10 text-sm" />
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto p-4 space-y-3">
                  {loading ? (
                    <div className="flex items-center justify-center h-40">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                    </div>
                  ) : searchQuery ? (
                    filteredClauses.length === 0 ? (
                      <p className="text-sm text-slate-500 text-center py-8">Aucune clause trouvée</p>
                    ) : (
                      <div className="space-y-2">
                        {filteredClauses.map((clause) => (
                          <ClauseRow key={clause.id} clause={clause} onAdd={() => { onAddClause(clause); toast.success("Clause ajoutée"); }} onEdit={() => openEdit(clause)} onDelete={() => handleDelete(clause)} getLocalizedText={getLocalizedText} />
                        ))}
                      </div>
                    )
                  ) : (
                    <div className="space-y-2">
                      {categories.map((category) => {
                        const categoryClauses = clausesByCategory[category] || [];
                        if (categoryClauses.length === 0) return null;
                        const isExpanded = expandedCategories[category];
                        return (
                          <div key={category} className="border border-slate-200 rounded-lg overflow-hidden">
                            <button onClick={() => toggleCategory(category)}
                              className="w-full flex items-center justify-between p-3 bg-slate-50 hover:bg-slate-100 transition">
                              <span className="text-sm font-medium text-slate-900">{category}</span>
                              <div className="flex items-center gap-2">
                                <Badge variant="outline" className="text-xs">{categoryClauses.length}</Badge>
                                {isExpanded ? <ChevronDown className="h-4 w-4 text-slate-500" /> : <ChevronRight className="h-4 w-4 text-slate-500" />}
                              </div>
                            </button>
                            <AnimatePresence>
                              {isExpanded && (
                                <motion.div initial={{ height: 0 }} animate={{ height: "auto" }} exit={{ height: 0 }} className="overflow-hidden">
                                  <div className="p-2 space-y-1">
                                    {categoryClauses.map((clause) => (
                                      <ClauseRow key={clause.id} clause={clause} onAdd={() => { onAddClause(clause); toast.success("Clause ajoutée"); }} onEdit={() => openEdit(clause)} onDelete={() => handleDelete(clause)} getLocalizedText={getLocalizedText} />
                                    ))}
                                  </div>
                                </motion.div>
                              )}
                            </AnimatePresence>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </>
            )}

            {/* === FORM VIEW === */}
            {view === "form" && (
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                <div>
                  <Label className="text-xs font-semibold text-slate-700 mb-1 block">Titre (FR) *</Label>
                  <Input value={form.title.fr} onChange={e => setForm({...form, title: {...form.title, fr: e.target.value}})}
                    placeholder="Ex: Clause de résiliation" />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-slate-700 mb-1 block">Titre (EN)</Label>
                  <Input value={form.title.en} onChange={e => setForm({...form, title: {...form.title, en: e.target.value}})}
                    placeholder="Ex: Termination clause" />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-slate-700 mb-1 block">Catégorie</Label>
                  <select value={form.category} onChange={e => setForm({...form, category: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500">
                    {categories.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <Label className="text-xs font-semibold text-slate-700 mb-1 block">Description (FR)</Label>
                  <Input value={form.description.fr} onChange={e => setForm({...form, description: {...form.description, fr: e.target.value}})}
                    placeholder="Brève description de la clause" />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-slate-700 mb-1 block">Contenu (FR) *</Label>
                  <textarea value={form.content.fr} onChange={e => setForm({...form, content: {...form.content, fr: e.target.value}})}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm resize-none focus:ring-2 focus:ring-blue-500"
                    rows={8} placeholder="Texte complet de la clause juridique..." />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-slate-700 mb-1 block">Contenu (EN)</Label>
                  <textarea value={form.content.en} onChange={e => setForm({...form, content: {...form.content, en: e.target.value}})}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm resize-none focus:ring-2 focus:ring-blue-500"
                    rows={4} placeholder="English version of the clause..." />
                </div>
                <div className="flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <input type="checkbox" id="mandatory" checked={form.is_mandatory}
                    onChange={e => setForm({...form, is_mandatory: e.target.checked})}
                    className="h-4 w-4 rounded border-slate-300 text-blue-600" />
                  <label htmlFor="mandatory" className="text-sm text-blue-900 cursor-pointer">
                    Clause obligatoire (ne peut pas être supprimée du contrat)
                  </label>
                </div>
              </div>
            )}

            {/* Footer */}
            {view === "form" ? (
              <div className="p-4 border-t bg-slate-50 flex gap-3">
                <Button variant="outline" className="flex-1" onClick={() => setView("list")}>Annuler</Button>
                <Button className="flex-1 bg-blue-600 hover:bg-blue-700 gap-2" onClick={handleSave} disabled={saving}>
                  <Save className="h-4 w-4" />
                  {saving ? "Enregistrement..." : (editingClause ? "Mettre à jour" : "Créer la clause")}
                </Button>
              </div>
            ) : (
              <div className="p-4 border-t bg-slate-50">
                <p className="text-xs text-slate-600 text-center">
                  {clauses.length} clause(s) disponible(s)
                </p>
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

function ClauseRow({ clause, onAdd, onEdit, onDelete, getLocalizedText }) {
  return (
    <div className="flex items-start gap-1 p-2 rounded hover:bg-slate-50 group">
      <button onClick={onAdd} className="flex-1 text-left flex items-start gap-2 min-w-0">
        <Plus className="h-4 w-4 text-slate-400 group-hover:text-blue-600 mt-0.5 flex-shrink-0" />
        <div className="min-w-0">
          <p className="text-sm font-medium text-slate-900 truncate">{getLocalizedText(clause.title)}</p>
          <p className="text-xs text-slate-500 line-clamp-1 mt-0.5">{getLocalizedText(clause.description)}</p>
        </div>
      </button>
      <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition flex-shrink-0">
        <button onClick={e => { e.stopPropagation(); onEdit(); }}
          className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded transition">
          <Edit3 className="h-3.5 w-3.5" />
        </button>
        <button onClick={e => { e.stopPropagation(); onDelete(); }}
          className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded transition">
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}
